import { t } from '@lingui/core/macro';
import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from 'modules/match-video/domain/assembler/constants';
import { ReviewCanvasContext, ReviewScene } from 'modules/match-video/domain/interfaces';
import {
  blockWidth,
  Box,
  browserSceneEnv,
  coverInk,
  drawRisingFromLine,
  fitTeamPair,
  fillBlock,
  MASK_PAD_EM,
  measureBlock,
  RISE_DISTANCE,
  SceneEnv,
} from 'modules/match-video/domain/scenes/kit';
import { DangerPlateData } from 'modules/match-video/domain/scenes/scene-data';
import { beginBody, clipTagWipe, drawTag } from './plate-parts';
import { nightDangerPlateState } from './plate-state';
import { NIGHT, NIGHT_FONTS, orangeAlpha, PLATE_BOTTOM, PLATE_LEFT } from './style';

// `.a-dplate` and its parts.
const HEIGHT = 110;
const TOP = OUTPUT_HEIGHT - PLATE_BOTTOM - HEIGHT;
const CENTER_Y = TOP + HEIGHT / 2;
const RIGHT_LIMIT = OUTPUT_WIDTH - PLATE_LEFT;
const TAG = { size: 40, min: 28, maxWidth: 700, padX: 30, spacing: 1 };
const BODY = { padX: 36 };
/** «Команда × Команда»: the cross has 16 px of air on each side. */
const TEXT = { size: 30, min: 22, crossMargin: 16 };

export const NIGHT_DANGER_PLATE_FONTS: readonly string[] = [NIGHT_FONTS.tag(TAG.size), NIGHT_FONTS.danger(TEXT.size)];

/** The pulse: an orange wave growing out of the tag's edges (`box-shadow: 0 0 0 <spread>`), outside the tag only. */
function drawPulse(ctx: ReviewCanvasContext, tag: Box, spread: number, alpha: number): void {
  if (alpha <= 0 || spread <= 0) return;
  ctx.beginPath();
  ctx.rect(tag.left - spread, tag.top - spread, tag.width + 2 * spread, tag.height + 2 * spread);
  ctx.rect(tag.left, tag.top, tag.width, tag.height);
  ctx.fillStyle = orangeAlpha(alpha);
  ctx.fill('evenodd');
}

/**
 * Set A dangerous-moment plate: the orange «ОПАСНЫЙ МОМЕНТ» tag wipes in and pulses twice, the dark body with
 * «Команда × Команда» unfolds out of it. Exit as the goal plate.
 */
export async function createNightDangerPlate(data: DangerPlateData, env: SceneEnv = browserSceneEnv): Promise<ReviewScene> {
  await env.loadFonts(NIGHT_DANGER_PLATE_FONTS);
  const measurer = env.createCanvas(1, 1);
  const m = measurer.ctx;
  const tagText = measureBlock(m, t`Опасный момент`.toLocaleUpperCase(), {
    maxWidth: TAG.maxWidth,
    size: TAG.size,
    min: TAG.min,
    font: NIGHT_FONTS.tag,
    lineHeight: HEIGHT / TAG.size,
    spacing: TAG.spacing,
  });
  const tag: Box = { left: PLATE_LEFT, top: TOP, width: blockWidth(tagText) + 2 * TAG.padX, height: HEIGHT };
  const bodyLeft = tag.left + tag.width;
  const teams = fitTeamPair(m, data.home.name, data.away.name, {
    maxWidth: Math.max(0, RIGHT_LIMIT - bodyLeft - 2 * BODY.padX),
    size: TEXT.size,
    min: TEXT.min,
    font: NIGHT_FONTS.danger,
    separator: '×',
    gap: TEXT.crossMargin,
  });

  const body: Box = { left: bodyLeft, top: TOP, width: teams.width + 2 * BODY.padX, height: HEIGHT };
  const lineHeight = teams.home.lineHeight;
  const textTop = CENTER_Y - (lineHeight + MASK_PAD_EM * TEXT.size) / 2;
  const textLeft = bodyLeft + BODY.padX;
  const lineBox = { x: textLeft, y: textTop, width: teams.width, height: lineHeight + MASK_PAD_EM * TEXT.size };
  const mask = coverInk(m, lineBox, [teams.home, teams.separator, teams.away]);
  measurer.release();

  return {
    draw: (ctx, timeSec, windowSec) => {
      const s = nightDangerPlateState(timeSec, windowSec);
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';

      ctx.save();
      if (beginBody(ctx, body, s.bodyScaleX, NIGHT.ink) && s.textAlpha > 0) {
        ctx.globalAlpha = s.textAlpha;
        drawRisingFromLine(
          ctx,
          mask,
          s.textRise,
          (c) => {
            let x = textLeft;
            c.fillStyle = NIGHT.dangerText;
            fillBlock(c, teams.home, x, textTop, 'left');
            x += blockWidth(teams.home) + teams.gap;
            c.fillStyle = NIGHT.orange;
            fillBlock(c, teams.separator, x, textTop, 'left');
            x += blockWidth(teams.separator) + teams.gap;
            c.fillStyle = NIGHT.dangerText;
            fillBlock(c, teams.away, x, textTop, 'left');
          },
          RISE_DISTANCE * lineHeight,
        );
      }
      ctx.restore();

      if (s.tagReveal > 0) {
        ctx.save();
        clipTagWipe(ctx, tag, s.tagReveal, false);
        drawTag(ctx, tag, tagText, TAG.padX, NIGHT.orange);
        if (s.pulse) drawPulse(ctx, tag, s.pulse.spread, s.pulse.alpha);
        ctx.restore();
      }
    },
    dispose: () => undefined,
  };
}
